import { useEffect, useState, type FormEvent } from 'react';
import type { AppAuthApi } from './App.js';
import type { WatchSettings, WatchedChannelView } from './auth/auth-api.js';

/**
 * Страница настроек слежения (/admin/settings): за какими каналами следить, как часто проверять,
 * какие ролики пропускать и куда слать дайджест. Открыта только администратору.
 */

/** Минуты в поле формы — секунды в API: настройки правит человек, а не машина. */
function toMinutes(seconds: number | null): string {
  return seconds === null ? '' : String(Math.round(seconds / 60));
}

function toSeconds(minutes: string): number | null {
  const trimmed = minutes.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed * 60)) : null;
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ChannelRow({
  channel,
  onToggle,
  onRemove,
  busy,
}: {
  channel: WatchedChannelView;
  onToggle: (channel: WatchedChannelView) => void;
  onRemove: (channel: WatchedChannelView) => void;
  busy: boolean;
}) {
  return (
    <li className={`channel${channel.enabled ? '' : ' channel--off'}`}>
      <div className="channel__body">
        <p className="channel__title">{channel.title ?? channel.youtubeChannelId}</p>
        <p className="channel__meta">
          проверен {formatWhen(channel.lastCheckedAt)} · новое с {formatWhen(channel.lastPublishedAt)}
        </p>
        {channel.lastError && <p className="channel__error">⚠️ {channel.lastError}</p>}
      </div>
      <div className="channel__actions">
        <button
          className="channel__button"
          type="button"
          disabled={busy}
          onClick={() => onToggle(channel)}
        >
          {channel.enabled ? 'Выключить' : 'Включить'}
        </button>
        <button
          className="channel__button channel__button--danger"
          type="button"
          disabled={busy}
          onClick={() => onRemove(channel)}
        >
          Удалить
        </button>
      </div>
    </li>
  );
}

type PageState =
  | { status: 'loading' }
  | { status: 'ready' }
  | { status: 'error'; message: string };

export function SettingsPage({ api, onBack }: { api: AppAuthApi; onBack: () => void }) {
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [channels, setChannels] = useState<WatchedChannelView[]>([]);
  const [interval, setInterval] = useState('30');
  const [chatId, setChatId] = useState('');
  const [minMinutes, setMinMinutes] = useState('5');
  const [maxMinutes, setMaxMinutes] = useState('');
  const [sttEnabled, setSttEnabled] = useState(false);
  const [channelUrl, setChannelUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  function applySettings(settings: WatchSettings) {
    setInterval(String(settings.checkIntervalMinutes));
    setChatId(settings.defaultDeliveryChatId ?? '');
    setMinMinutes(toMinutes(settings.defaultMinDurationSeconds));
    setMaxMinutes(toMinutes(settings.defaultMaxDurationSeconds));
    setSttEnabled(settings.sttEnabled);
  }

  useEffect(() => {
    let active = true;

    api
      .getAdminSettings()
      .then((payload) => {
        if (!active) return;
        applySettings(payload.settings);
        setChannels(payload.channels);
        setState({ status: 'ready' });
      })
      .catch((error: unknown) => {
        if (!active) return;
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : 'Не удалось открыть настройки',
        });
      });

    return () => {
      active = false;
    };
  }, [api]);

  async function run(action: () => Promise<string>) {
    setBusy(true);
    setNote(null);
    try {
      setNote({ kind: 'ok', text: await action() });
    } catch (error) {
      setNote({
        kind: 'error',
        text: error instanceof Error ? error.message : 'Не получилось',
      });
    } finally {
      setBusy(false);
    }
  }

  async function saveSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await run(async () => {
      const saved = await api.saveAdminSettings({
        checkIntervalMinutes: Math.max(1, Number(interval) || 30),
        defaultDeliveryChatId: chatId.trim() === '' ? null : chatId.trim(),
        defaultMinDurationSeconds: toSeconds(minMinutes) ?? 0,
        defaultMaxDurationSeconds: toSeconds(maxMinutes),
        sttEnabled,
      });
      applySettings(saved);
      return 'Настройки сохранены';
    });
  }

  async function addChannel(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const url = channelUrl.trim();
    if (url.length === 0) return;

    await run(async () => {
      const channel = await api.addChannel(url);
      setChannels((current) => [...current, channel]);
      setChannelUrl('');
      return `Слежу за каналом «${channel.title ?? channel.youtubeChannelId}»`;
    });
  }

  async function toggleChannel(channel: WatchedChannelView) {
    await run(async () => {
      const updated = await api.updateChannel(channel.id, { enabled: !channel.enabled });
      setChannels((current) =>
        current.map((item) => (item.id === channel.id ? updated : item)),
      );
      return updated.enabled ? 'Канал включён' : 'Канал выключен';
    });
  }

  async function removeChannel(channel: WatchedChannelView) {
    await run(async () => {
      await api.removeChannel(channel.id);
      setChannels((current) => current.filter((item) => item.id !== channel.id));
      return 'Канал удалён';
    });
  }

  async function checkNow() {
    await run(async () => {
      await api.checkChannelsNow();
      return 'Проверяю каналы — новые ролики появятся в списке разборов';
    });
  }

  if (state.status === 'loading') {
    return (
      <section className="state-card state-card--center" role="status">
        <span className="loader" aria-hidden="true" />
        <p>Открываем настройки…</p>
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="state-card state-card--error" role="alert">
        <h1>Не удалось открыть настройки</h1>
        <p>{state.message}</p>
        <button className="button button--ghost" type="button" onClick={onBack}>
          К разборам
        </button>
      </section>
    );
  }

  return (
    <section className="settings">
      <div className="settings__head">
        <div>
          <p className="eyebrow">Настройки</p>
          <h1>Слежение за каналами</h1>
        </div>
        <button className="channel__button" type="button" onClick={onBack}>
          ← К разборам
        </button>
      </div>

      {note && (
        <p
          className={`analysis-form__note analysis-form__note--${note.kind === 'ok' ? 'ok' : 'error'}`}
          role={note.kind === 'ok' ? 'status' : 'alert'}
        >
          {note.text}
        </p>
      )}

      <div className="settings__block">
        <p className="eyebrow">Каналы</p>

        <form className="analysis-form" onSubmit={addChannel}>
          <div className="analysis-form__row">
            <input
              className="analysis-form__input"
              type="url"
              inputMode="url"
              placeholder="https://www.youtube.com/@канал"
              aria-label="Ссылка на канал"
              value={channelUrl}
              onChange={(event) => setChannelUrl(event.target.value)}
              disabled={busy}
            />
            <button className="button" type="submit" disabled={busy || channelUrl.trim() === ''}>
              Добавить
            </button>
          </div>
        </form>

        {channels.length === 0 ? (
          <p className="settings__empty">
            Пока ни одного канала. Добавь ссылку — новые ролики начнут приходить в чат
            с пересказом.
          </p>
        ) : (
          <ul className="channels">
            {channels.map((channel) => (
              <ChannelRow
                key={channel.id}
                channel={channel}
                busy={busy}
                onToggle={toggleChannel}
                onRemove={removeChannel}
              />
            ))}
          </ul>
        )}

        {channels.length > 0 && (
          <button
            className="button button--ghost settings__check"
            type="button"
            disabled={busy}
            onClick={checkNow}
          >
            Проверить сейчас
          </button>
        )}
      </div>

      <form className="settings__block" onSubmit={saveSettings}>
        <p className="eyebrow">Общие правила</p>

        <label className="settings__field">
          <span>Проверять каналы каждые, мин</span>
          <input
            className="analysis-form__input"
            type="number"
            min="1"
            value={interval}
            onChange={(event) => setInterval(event.target.value)}
            disabled={busy}
          />
        </label>

        <label className="settings__field">
          <span>Chat ID для дайджестов</span>
          <input
            className="analysis-form__input"
            type="text"
            inputMode="numeric"
            placeholder="например 1327157499"
            value={chatId}
            onChange={(event) => setChatId(event.target.value)}
            disabled={busy}
          />
        </label>

        <label className="settings__field">
          <span>Пропускать короче, мин</span>
          <input
            className="analysis-form__input"
            type="number"
            min="0"
            value={minMinutes}
            onChange={(event) => setMinMinutes(event.target.value)}
            disabled={busy}
          />
        </label>

        <label className="settings__field">
          <span>Пропускать длиннее, мин</span>
          <input
            className="analysis-form__input"
            type="number"
            min="0"
            placeholder="без ограничения"
            value={maxMinutes}
            onChange={(event) => setMaxMinutes(event.target.value)}
            disabled={busy}
          />
        </label>

        <label className="settings__toggle">
          <input
            type="checkbox"
            checked={sttEnabled}
            onChange={(event) => setSttEnabled(event.target.checked)}
            disabled={busy}
          />
          <span>
            Распознавать речь, если у ролика нет субтитров
            <small>Требует ключа SaluteSpeech; без него ролик без субтитров пропускается</small>
          </span>
        </label>

        <button className="button" type="submit" disabled={busy}>
          {busy ? 'Сохраняем…' : 'Сохранить'}
        </button>
      </form>
    </section>
  );
}
